import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Eye,
  Package,
  Calendar,
  DollarSign
} from 'lucide-react';
import { purchaseReturnsAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import DataTable from '../ui/DataTable';
import dayjs from '../../utils/dayjs';
import { readParam, useSyncUrlParams } from '../../hooks/useUrlListState';

const PurchaseReturnList = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { addNotification } = useAppStore();
  
  // Search/filter/sort/pagination state is seeded from the URL so the view is
  // shareable and restored when returning from a return's detail page.
  const [searchParams] = useSearchParams();
  const [purchaseReturns, setPurchaseReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Applied query = what actually drives the fetch.
  const [searchTerm, setSearchTerm] = useState(() => readParam(searchParams, 'q', ''));
  const [isSearching, setIsSearching] = useState(() => Boolean(readParam(searchParams, 'q', '')));
  const [startDate, setStartDate] = useState(() => readParam(searchParams, 'start', ''));
  const [endDate, setEndDate] = useState(() => readParam(searchParams, 'end', ''));
  const [sortBy, setSortBy] = useState(() => readParam(searchParams, 'sort', 'ID'));
  const [sortOrder, setSortOrder] = useState(() => readParam(searchParams, 'order', 'desc'));

  // Draft inputs: what's typed into the search box / date fields BEFORE the user
  // hits Search. Committed to the applied query above only by handleSearch.
  const [draftSearch, setDraftSearch] = useState(() => readParam(searchParams, 'q', ''));
  const [draftStartDate, setDraftStartDate] = useState(() => readParam(searchParams, 'start', ''));
  const [draftEndDate, setDraftEndDate] = useState(() => readParam(searchParams, 'end', ''));

  // Pagination state
  const [currentPage, setCurrentPage] = useState(() => readParam(searchParams, 'page', 1, 'number'));
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => readParam(searchParams, 'size', 20, 'number'));
  const searchInputRef = React.useRef(null);
  // Skip the first run of the applied-change / sort effects so state restored
  // from the URL isn't re-loaded or reset to page 1 on mount.
  const appliedInitRef = React.useRef(false);
  const sortInitRef = React.useRef(false);

  // Mirror the current view into the URL (defaults omitted).
  useSyncUrlParams(
    { q: searchTerm.trim(), sort: sortBy, order: sortOrder, start: startDate, end: endDate, page: currentPage, size: itemsPerPage },
    { q: '', sort: 'ID', order: 'desc', start: '', end: '', page: 1, size: 20 },
  );

  // Load purchase returns (pattern similar to PurchaseList)
  const loadData = React.useCallback(async (page = 1, limit = 20, search = '') => {
    try {
      setLoading(true);
      setError(null);
      const skip = (page - 1) * limit;
      const params = { skip, limit, sort: sortBy, order: sortOrder };
      if (search && search.trim()) {
        params.q = search.trim(); // backend may ignore; used for future compatibility
      }
      if (startDate) {
        params.start_date = new Date(startDate).toISOString();
      }
      if (endDate) {
        // include entire day by setting to end of day if time not provided
        const end = new Date(endDate);
        if (endDate.length <= 10) {
          end.setHours(23, 59, 59, 999);
        }
        params.end_date = end.toISOString();
      }
      const response = await purchaseReturnsAPI.list(params);
      const list = response.data || [];

      // Sorting is done server-side (params.sort / params.order).
      setPurchaseReturns(list);
      // naive next-page check (until backend returns total)
      if ((response.data || []).length === limit) {
        setTotalPages(page + 1);
      } else {
        setTotalPages(page);
      }
    } catch (err) {
      setError(err);
      setPurchaseReturns([]);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load purchase returns'
      });
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, startDate, endDate]);

  // Delete purchase return
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this purchase return?')) {
      try {
        await purchaseReturnsAPI.remove(id);
        addNotification({
          type: 'success',
          title: 'Success',
          message: 'Purchase return deleted successfully'
        });
        loadData(currentPage, itemsPerPage, searchTerm); // Refresh the list
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: error.response?.data?.detail || 'Failed to delete purchase return'
        });
      }
    }
  };

  // Reload when the APPLIED search/date filters change (Search / Clear), not as
  // you type. Skips the first run so the mount load below isn't duplicated.
  useEffect(() => {
    if (!appliedInitRef.current) {
      appliedInitRef.current = true;
      return;
    }
    setCurrentPage(1);
    setIsSearching(Boolean(searchTerm.trim()));
    loadData(1, itemsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, startDate, endDate]);

  // Re-query (server-side sort) when the sort changes; keep the current page.
  useEffect(() => {
    if (!sortInitRef.current) {
      sortInitRef.current = true;
      return;
    }
    loadData(currentPage, itemsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  useEffect(() => {
    // Initial load respects the page/search/filters restored from the URL.
    loadData(currentPage, itemsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Column definitions for DataTable
  const columns = [
    {
      key: 'ID',
      title: 'Return #',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-red-600" />
          <span className="text-sm font-medium text-gray-900">Ret-{value}</span>
        </div>
      )
    },
    {
      key: 'PurchaseReturnDate',
      title: 'Date',
      sortable: true,
      render: (value) => {
        return (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-900">
              {value ? dayjs(value).format('MMM DD, YYYY') : '-'}
            </span>
          </div>
        );
      }
    },
    {
      key: 'Remarks',
      title: 'Remarks',
      sortable: false,
      render: (value) => (
        <div className="text-sm text-gray-900 max-w-xs truncate">
          {value || '-'}
        </div>
      )
    },
    {
      key: 'itemsCount',
      title: 'Items',
      sortable: false,
      render: (value, row) => (
        <div className="text-sm text-gray-900">
          {value || 0} items
        </div>
      )
    },
    {
      key: 'totalAmount',
      title: 'Total Amount',
      sortable: false,
      render: (value, row) => {
  
        return (
          <div className="text-sm text-gray-900">
            {formatPrice(value)}
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: 'Actions',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/purchase-returns/${row.ID}`)}
            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadData(page, itemsPerPage, searchTerm);
  };

  const handleItemsPerPageChange = (e) => {
    const limit = parseInt(e.target.value);
    setItemsPerPage(limit);
    setCurrentPage(1);
    loadData(1, limit, searchTerm);
  };

  // Apply the typed search + date filters (Search button / Enter). The applied-
  // change effect does the fetch and page reset — nothing loads as you type.
  const handleSearch = () => {
    setSearchTerm(draftSearch);
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
  };

  const clearSearch = () => {
    setDraftSearch('');
    setSearchTerm('');
  };

  const clearAll = () => {
    setDraftSearch('');
    setDraftStartDate('');
    setDraftEndDate('');
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
  };

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Purchase Returns</h3>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={() => loadData(1, itemsPerPage, searchTerm)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const displayData = purchaseReturns;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Return Management</h1>
          <p className="text-gray-600">Manage your purchase returns and stock adjustments</p>
        </div>
        <button
          onClick={() => navigate('/purchase-returns/new')}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          New Purchase Return
        </button>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* Main Search Bar */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by return ID or remarks..."
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
              style={{ fontSize: '1rem' }}
              className="w-full h-12 pl-11 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {draftSearch && (
              <button
                onClick={clearSearch}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="h-12 px-6 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium whitespace-nowrap"
          >
            <Search className="h-5 w-5" />
            Search
          </button>
        </div>

        {/* Advanced Filters */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Advanced filters</h3>
            <button
              onClick={clearAll}
              className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear all
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
              <input
                type="date"
                value={draftStartDate}
                onChange={(e) => setDraftStartDate(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
              <input
                type="date"
                value={draftEndDate}
                onChange={(e) => setDraftEndDate(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {isSearching && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <p className="text-blue-800 text-sm">Showing {purchaseReturns.length} search results for "{searchTerm}"</p>
              <button onClick={clearSearch} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Clear Search</button>
            </div>
          </div>
        )}
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={displayData}
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={displayData.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        itemLabel="purchase returns"
        onRowClick={(row, e) =>
          e.metaKey || e.ctrlKey
            ? window.open(`/purchase-returns/${row.ID}`, '_blank', 'noopener')
            : navigate(`/purchase-returns/${row.ID}`)
        }
        className="bg-white rounded-xl shadow-sm border border-gray-200"
      />
    </div>
  );
};

export default PurchaseReturnList;